import { Component, ElementRef, input, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Alert, AlertService } from '@michelin/theme';
import { AutomakerDto } from 'src/app/shared/models/automaker/automaker-dto';
import { CompanyDto, CompanyNoIdDto } from 'src/app/shared/models/company/company-dto';
import { DistributorDto } from 'src/app/shared/models/distributor/distributor-dto';
import { CityDto } from 'src/app/shared/models/location/city-dto';
import { StateDto } from 'src/app/shared/models/location/state-dto';
import { LocationService } from 'src/app/shared/services/location/location.service';

@Component({
    selector: 'app-company-form',
    templateUrl: './company-form.component.html',
    standalone: false
})
export class CompanyFormComponent implements OnInit {
  loading = false;
  form: FormGroup = null;
  cities: CityDto[] = [];
  automakerFilteredOptions: AutomakerDto[] = [];
  distributorFilteredOptions: DistributorDto[] = [];
  stateFilteredOptions: StateDto[] = [];
  cityFilteredOptions: CityDto[] = [];

  @Input() readonly: boolean = false;
  @Input() states: StateDto[] = [];
  @Input({ required: true }) parentFormGroup: FormGroup;

  @ViewChild('stateInput') stateInput: ElementRef<HTMLInputElement>;
  @ViewChild('cityInput') cityInput: ElementRef<HTMLInputElement>;

  constructor(
    public readonly formBuilder: FormBuilder,
    private readonly alertService: AlertService,
    private readonly locationService: LocationService
  ) {
    this.form = this.formBuilder.group({
      companyName: ['', Validators.required],
      tradeName: ['', Validators.required],
      document: ['', Validators.required],
      stateRegistration: [''],
      hasStateRegistrationExemption: [false, Validators.required],
      address: this.formBuilder.group({
        zipCode: ['', Validators.required],
        state: ['', Validators.required],
        city: ['', Validators.required],
        neighborhood: ['', Validators.required],
        streetName: ['', Validators.required],
        complement: [''],
        phone: ['']
      })
    });
  }

  get stateRegistration(): FormControl {
    return this.form.get('stateRegistration') as FormControl;
  }

  get hasStateRegistrationExemption(): FormControl {
    return this.form.get('hasStateRegistrationExemption') as FormControl;
  }

  get state(): FormControl {
    return this.form.get('address').get('state') as FormControl;
  }

  get city(): FormControl {
    return this.form.get('address').get('city') as FormControl;
  }

  get formValues(): CompanyNoIdDto {
    return {
      companyName: this.form.value.companyName,
      tradeName: this.form.value.tradeName,
      document: this.form.value.document,
      stateRegistration: this.form.value.stateRegistration,
      hasStateRegistrationExemption: this.form.value.hasStateRegistrationExemption,
      address: {
        fullAddress: this.form.value.address?.streetName,
        neighborhood: this.form.value.address?.neighborhood,
        complement: this.form.value.address?.complement,
        zipCode: this.form.value.address?.zipCode,
        phone: this.form.value.address?.phone,
        cityId: this.form.value.address?.city?.id
      }
    };
  }

  loadFormValues(data: CompanyDto) {
    this.form.patchValue({
      companyName: data.companyName,
      tradeName: data.tradeName,
      document: data.document,
      stateRegistration: data.stateRegistration,
      hasStateRegistrationExemption: data.hasStateRegistrationExemption,
      address: {
        streetName: data.address.fullAddress,
        neighborhood: data.address.neighborhood,
        complement: data.address.complement,
        zipCode: data.address.zipCode,
        phone: data.address.phone,
        state: data.address.city?.state,
        city: data.address?.city
      }
    });
  }

  stateDisplayName(state: any): string {
    return state ? `${state.name} (${state.code})` : '';
  }

  ngOnInit() {
    if (this.readonly) {
      this.form.disable();
    }

    this.parentFormGroup?.addControl('company', this.form);

    this.hasStateRegistrationExemption.valueChanges.subscribe((value) => {
      if (value) {
        this.stateRegistration.reset();
        this.stateRegistration.disable();
      } else {
        this.stateRegistration.enable();
      }
    });

    this.state.valueChanges.subscribe((state) => {
      this.cities = [];
      this.city.reset();
      if (state) {
        this.loading = true;
        this.locationService
          .findCitiesByState(state.id)
          .subscribe({
            next: (result) => (this.cities = result),
            error: () => this.alertService.showAlert({ type: 'critical', title: 'Ocorreu um erro ao carregar as cidades' } as Alert)
          })
          .add(() => (this.loading = false));
      }
    });
  }
}
